import type { FetchResponse } from 'openapi-fetch';
import type { SuccessResponse } from 'openapi-typescript-helpers';
import type { paths } from './coreApiSchema';
import { getItem } from '../utils/storage';
import {
  getLegacyUserById,
  getLegacyUserPermissions,
  getLegacyUserSettings,
  updateLegacyUser,
  updateLegacyUserSettings,
} from './legacyUsers';
import { convertIsoDateToLegacy } from './legacyCommonMappers';
import { shouldUseCoreApi } from '../utils/features';
import { API_VERSION } from '../config/app';
import { coreApiClient } from './apiClient';

export type User = SuccessResponse<paths['/core/v1/users/{userId}']['get']['responses']>['data'];
export type UserPatchBody = NonNullable<
  paths['/core/v1/users/{userId}']['patch']['requestBody']
>['content']['application/json'];
export type UserSettings = SuccessResponse<
  paths['/core/v1/users/{userId}/settings']['get']['responses']
>['data'];
export type PreferredLanguage = NonNullable<
  SuccessResponse<
    paths['/core/v1/users/{userId}/settings']['get']['responses']
  >['data']['preferredLanguage']
>;
export type UserSettingsPatchBody = NonNullable<
  paths['/core/v1/users/{userId}/settings']['patch']['requestBody']
>['content']['application/json'];
export type UserPermissions = SuccessResponse<
  paths['/core/v1/users/{userId}/permissions']['get']['responses']
>['data'];

export const getUserById = async ({
  userId,
  tenantId,
}: paths['/core/v1/users/{userId}']['get']['parameters']['path'] & {
  tenantId: number;
}): Promise<
  FetchResponse<paths['/core/v1/users/{userId}']['get'], { parseAs: 'json' }, 'application/json'>
> => {
  if (!shouldUseCoreApi()) {
    return getLegacyUserById(userId);
  }

  const userRes = await coreApiClient.GET('/core/v1/users/{userId}', {
    params: {
      path: { userId },
      header: {
        authorization: `Bearer ${getItem('coreApiAccessToken')}`,
        'cosafe-api-version': String(API_VERSION),
        'cosafe-tenant-id': String(tenantId),
      },
    },
  });
  if (userRes.error) return userRes;
  return {
    ...userRes,
    data: {
      ...userRes.data,
      data: {
        ...userRes.data.data,
        lastAccessAt: userRes.data.data.lastAccessAt
          ? convertIsoDateToLegacy(userRes.data.data.lastAccessAt)
          : null,
        unavailableUntil: userRes.data.data.unavailableUntil
          ? convertIsoDateToLegacy(userRes.data.data.unavailableUntil)
          : null,
      },
    },
  };
};

export const updateUser = async ({
  userId,
  tenantId,
  body,
  primaryPhoneNumber,
}: paths['/core/v1/users/{userId}']['patch']['parameters']['path'] & {
  tenantId: number;
  body: UserPatchBody;
  primaryPhoneNumber?: string; // TODO: Needed for legacy scenario. Can be removed after legacy code is removed
}): Promise<
  FetchResponse<paths['/core/v1/users/{userId}']['patch'], { parseAs: 'json' }, 'application/json'>
> => {
  if (!shouldUseCoreApi()) {
    return updateLegacyUser(body, primaryPhoneNumber);
  }

  return coreApiClient.PATCH('/core/v1/users/{userId}', {
    params: {
      path: { userId },
      header: {
        authorization: `Bearer ${getItem('coreApiAccessToken')}`,
        'cosafe-api-version': String(API_VERSION),
        'cosafe-tenant-id': String(tenantId),
      },
    },
    body,
  });
};

export const getUserSettings = async ({
  userId,
  tenantId,
}: paths['/core/v1/users/{userId}/settings']['get']['parameters']['path'] & {
  tenantId: number;
}): Promise<
  FetchResponse<
    paths['/core/v1/users/{userId}/settings']['get'],
    { parseAs: 'json' },
    'application/json'
  >
> => {
  if (!shouldUseCoreApi()) {
    return getLegacyUserSettings();
  }

  return coreApiClient.GET('/core/v1/users/{userId}/settings', {
    params: {
      path: { userId },
      header: {
        authorization: `Bearer ${getItem('coreApiAccessToken')}`,
        'cosafe-api-version': String(API_VERSION),
        'cosafe-tenant-id': String(tenantId),
      },
    },
  });
};

export const updateUserSettings = async ({
  userId,
  tenantId,
  body,
}: paths['/core/v1/users/{userId}/settings']['patch']['parameters']['path'] & {
  tenantId: number;
  body: UserSettingsPatchBody;
}): Promise<
  FetchResponse<
    paths['/core/v1/users/{userId}/settings']['patch'],
    { parseAs: 'json' },
    'application/json'
  >
> => {
  if (!shouldUseCoreApi()) {
    return updateLegacyUserSettings(body);
  }

  return coreApiClient.PATCH('/core/v1/users/{userId}/settings', {
    params: {
      path: { userId },
      header: {
        authorization: `Bearer ${getItem('coreApiAccessToken')}`,
        'cosafe-api-version': String(API_VERSION),
        'cosafe-tenant-id': String(tenantId),
      },
    },
    body,
  });
};

export const getUserPermissions = async ({
  userId,
  tenantId,
}: paths['/core/v1/users/{userId}/permissions']['get']['parameters']['path'] & {
  tenantId: number;
}): Promise<
  FetchResponse<
    paths['/core/v1/users/{userId}/permissions']['get'],
    { parseAs: 'json' },
    'application/json'
  >
> => {
  if (!shouldUseCoreApi()) {
    return getLegacyUserPermissions();
  }

  return coreApiClient.GET('/core/v1/users/{userId}/permissions', {
    params: {
      path: { userId },
      header: {
        authorization: `Bearer ${getItem('coreApiAccessToken')}`,
        'cosafe-api-version': String(API_VERSION),
        'cosafe-tenant-id': String(tenantId),
      },
    },
  });
};
